/**
 * Notification sound and browser notification utilities
 */

// Generate a simple notification sound using Web Audio API
export const playNotificationSound = () => {
  try {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    
    // Create oscillator for notification tone
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    // Configure sound - two-tone notification
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(880, audioContext.currentTime); // A5
    oscillator.frequency.setValueAtTime(1046.5, audioContext.currentTime + 0.15); // C6
    
    // Volume envelope
    gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
    gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.3);
    
    return true;
  } catch (error) {
    console.warn('Could not play notification sound:', error);
    return false;
  }
};

// Vibrate device if supported (mobile)
export const vibrate = (pattern = [100, 50, 100]) => {
  if ('vibrate' in navigator) {
    navigator.vibrate(pattern);
    return true;
  }
  return false;
};

// Request browser notification permission
export const requestNotificationPermission = async () => {
  if (!('Notification' in window)) {
    console.warn('Browser does not support notifications');
    return false;
  }
  
  if (Notification.permission === 'granted') {
    return true;
  }
  
  if (Notification.permission !== 'denied') {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  }
  
  return false;
};

// Show browser notification
export const showBrowserNotification = (title, options = {}) => {
  if (!('Notification' in window) || Notification.permission !== 'granted') {
    return null;
  }
  
  const notification = new Notification(title, {
    icon: '/favicon.ico',
    badge: '/favicon.ico',
    tag: 'dispatch-notification',
    renotify: true,
    ...options
  });
  
  // Auto-close after 5 seconds
  setTimeout(() => notification.close(), 5000);
  
  return notification;
};

// Combined notification: sound + vibration + browser notification
export const notify = async (title, body, playSound = true) => {
  try {
    // Play sound
    if (playSound) {
      playNotificationSound();
    }
    
    // Vibrate on mobile
    vibrate();
    
    // Show browser notification if permitted
    if ('Notification' in window && Notification.permission === 'granted') {
      showBrowserNotification(title, { body });
    }
  } catch (error) {
    console.warn('Notification error:', error);
  }
};

export default {
  playNotificationSound,
  vibrate,
  requestNotificationPermission,
  showBrowserNotification,
  notify
};
